/**
 * Elite motion.
 *
 * Separate from motion.js because it is BRANCH work, not site work: Basic must
 * keep the design it has, and everything here no-ops when its markup is
 * absent, so Basic pays one `querySelector` and nothing else.
 *
 * ── What this replaced ───────────────────────────────────────────────────
 * The previous version was a motion SIGNATURE: a SplitText line lift on the
 * headline, a magnetic cursor on the buttons, a self-drawing rule, and a foil
 * sweep. Material Design 3 has one motion primitive that belongs on a landing
 * page - the reveal - plus the state layer, which is pure CSS. Everything else
 * on that list was an effect rather than a behaviour, and effects are what
 * made the branch read as costume.
 *
 * Dropping SplitText also drops the whole class of defect it carried. The
 * masked version lost the descenders off "in Abdoun." because GSAP's mask
 * wrapper is one line box with overflow:clip; the unmasked version still left
 * wrappers behind whenever revert() did not run, and a surviving wrapper is
 * visible to text selection and to the accessibility tree, not just to the
 * eye. There is nothing left here to leave behind.
 *
 * ── The three rules, unchanged ───────────────────────────────────────────
 *
 *   1. ScrollTrigger is for SCRUB and PIN only. Reveals stay on
 *      IntersectionObserver, because ScrollTrigger caches its start and end at
 *      init and a late font or a lazy image silently kills the trigger. This
 *      file no longer imports it at all.
 *   2. Reduced motion returns to fully static AND COMPLETE. Never stuck at
 *      opacity 0, never frozen mid-rise.
 *   3. Nothing starts hidden until this file is definitely running. The hidden
 *      state lives behind `.el-js` on <html>, which is set below as the first
 *      act. No script, a broken script, a script that throws: the page is
 *      simply already visible.
 */

const REDUCED = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * The M3 reveal.
 *
 * Fade plus a 16px rise over 500ms on the decelerate curve, which is the
 * documented "enter" motion. One primitive, every section.
 *
 * Two floors under it, and both have been needed on this page before:
 *
 *   - a timeout that shows everything regardless. If no frame ever lands the
 *     observer callback the page must not stay blank.
 *   - a jumped-past sweep. IntersectionObserver fires on threshold CROSSINGS,
 *     so an element that goes from ratio 0 below the viewport to ratio 0 above
 *     it never crosses the threshold and the callback never runs. That happens
 *     on an anchor link, on a restored scroll position after back, on Cmd+End
 *     and on a fast flick. Measured here once: a single scrollTo(bottom) left
 *     eight elements invisible for the rest of the session.
 */
function initReveals() {
    const targets = document.querySelectorAll('[data-el-reveal]');
    if (!targets.length) return;

    const showAll = () => targets.forEach((el) => el.classList.add('in'));

    if (REDUCED()) {
        showAll();
        return;
    }

    // Only now is the hidden state armed. Order matters: set the class first,
    // and a slow frame paints a blank hero.
    document.documentElement.classList.add('el-js');

    const pending = new Set(targets);

    const reveal = (el) => {
        if (!pending.has(el)) return;
        pending.delete(el);
        io.unobserve(el);
        el.classList.add('in');
        if (pending.size === 0) window.removeEventListener('scroll', onScroll);
    };

    const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) reveal(entry.target);
        });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.08 });

    const onScroll = () => {
        const h = window.innerHeight;
        pending.forEach((el) => {
            const r = el.getBoundingClientRect();
            if (r.top < h && r.bottom > 0) reveal(el);
            else if (r.bottom <= 0) reveal(el); // scrolled clean past it
        });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    targets.forEach((el) => io.observe(el));

    // Last resort. Two and a half seconds is longer than any reveal and
    // shorter than anyone's patience.
    setTimeout(showAll, 2500);
}

/**
 * The M3 top app bar takes surface-container and elevation 2 once the page
 * moves, and drops back to surface at the top.
 *
 * Not a reveal and not gated on reduced motion: it is a state change on a
 * 300ms colour transition, which is the same class of thing as a hover.
 */
function initAppBar() {
    const nav = document.querySelector('[data-el-appbar]');
    if (!nav) return;

    const sync = () => nav.classList.toggle('stuck', window.scrollY > 8);
    sync();
    window.addEventListener('scroll', sync, { passive: true });
}

export function initEliteMotion() {
    // Branch check, and it is the only one. Basic never emits either hook.
    if (!document.querySelector('[data-el-reveal], [data-el-appbar]')) return;

    initReveals();
    initAppBar();
}
